// Projekt-Mitglieder-Store für den ShareDialog. Pro geöffnetem Dialog wird die
// Liste über GET /api/projects/:id/members geladen; Mutationen sind optimistisch
// und rollen bei Fehler per Refetch zurück. Fehler werden vom api()-Wrapper
// getoastet.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { ProjectMember, ProjectMemberRole } from "@juuplan/shared";
import { api } from "../lib/api";

export interface MembersState {
  loading: boolean;
  items: ProjectMember[];
  error: string | null;
  projectId: number | null;
}

const initial: MembersState = {
  loading: true,
  items: [],
  error: null,
  projectId: null,
};
const membersSignal: Signal<MembersState> = signal(initial);
let inFlight: Promise<void> | null = null;

async function loadMembers(projectId: number): Promise<void> {
  membersSignal.value = { ...membersSignal.value, loading: true, error: null, projectId };
  try {
    const items = await api<ProjectMember[]>(`/api/projects/${projectId}/members`);
    membersSignal.value = { loading: false, items, error: null, projectId };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown error";
    membersSignal.value = { loading: false, items: [], error: message, projectId };
  } finally {
    inFlight = null;
  }
}

/** Hook used by ShareDialog — (re)loads members when the project id changes. */
export function useMembers(projectId: number): MembersState {
  useEffect(() => {
    // Immer neu laden wenn der Dialog für ein (anderes) Projekt geöffnet wird —
    // Membership kann sich zwischen Öffnungen geändert haben.
    inFlight = loadMembers(projectId);
  }, [projectId]);
  return membersSignal.value;
}

export function refreshMembers(projectId: number): Promise<void> {
  inFlight = loadMembers(projectId);
  return inFlight;
}

export async function addMember(
  projectId: number,
  userId: number,
  role: ProjectMemberRole
): Promise<void> {
  const created = await api<ProjectMember>(`/api/projects/${projectId}/members`, {
    method: "POST",
    body: { user_id: userId, role },
  });
  // Optimistisch anhängen (falls noch derselbe Dialog offen ist).
  const state = membersSignal.value;
  if (state.projectId === projectId) {
    membersSignal.value = {
      ...state,
      items: [...state.items.filter((m) => m.user.id !== userId), created],
    };
  }
}

export async function updateMemberRole(
  projectId: number,
  userId: number,
  role: ProjectMemberRole
): Promise<void> {
  // Optimistisch.
  const state = membersSignal.value;
  const prev = state.items;
  if (state.projectId === projectId) {
    membersSignal.value = {
      ...state,
      items: state.items.map((m) => (m.user.id === userId ? { ...m, role } : m)),
    };
  }
  try {
    await api(`/api/projects/${projectId}/members/${userId}`, {
      method: "PATCH",
      body: { role },
    });
  } catch (err) {
    // Rollback.
    if (membersSignal.value.projectId === projectId) {
      membersSignal.value = { ...membersSignal.value, items: prev };
    }
    throw err;
  }
}

export async function removeMember(projectId: number, userId: number): Promise<void> {
  const state = membersSignal.value;
  const prev = state.items;
  if (state.projectId === projectId) {
    membersSignal.value = {
      ...state,
      items: state.items.filter((m) => m.user.id !== userId),
    };
  }
  try {
    await api(`/api/projects/${projectId}/members/${userId}`, { method: "DELETE" });
  } catch (err) {
    if (membersSignal.value.projectId === projectId) {
      membersSignal.value = { ...membersSignal.value, items: prev };
    }
    throw err;
  }
}

// ---------- read-only per-project cache (used by AssigneePicker, #875) ----------
//
// `useMembers` above is a single global slot owned by ShareDialog: one dialog
// open at a time, and it deliberately refetches on every mount. AssigneePicker
// needs the opposite shape — potentially many pickers reading the SAME
// project's members concurrently (every row in a project's task table) —
// where re-fetching per picker would thrash the network for no reason. This
// is a small keyed cache, separate from `membersSignal`, so it cannot regress
// ShareDialog's optimistic-mutation behavior above. Read-only: it does not
// expose add/update/remove and does not react to mutations made through
// `useMembers` — acceptable here since AssigneePicker only needs "who can I
// default-assign to", not a live membership editor.

interface ProjectMembersCacheEntry {
  loading: boolean;
  items: ProjectMember[];
}

const membersCache = new Map<number, Signal<ProjectMembersCacheEntry>>();
const cacheInFlight = new Set<number>();

function getCacheSignal(projectId: number): Signal<ProjectMembersCacheEntry> {
  let sig = membersCache.get(projectId);
  if (!sig) {
    sig = signal<ProjectMembersCacheEntry>({ loading: true, items: [] });
    membersCache.set(projectId, sig);
  }
  return sig;
}

async function loadMembersCached(projectId: number): Promise<void> {
  try {
    const items = await api<ProjectMember[]>(`/api/projects/${projectId}/members`);
    getCacheSignal(projectId).value = { loading: false, items };
  } catch {
    // Read-only cache: on error, just stop loading with an empty list so
    // callers fall back to "no known members" (AssigneePicker treats that as
    // "show all users") rather than getting toasted or stuck spinning.
    getCacheSignal(projectId).value = { loading: false, items: [] };
  } finally {
    cacheInFlight.delete(projectId);
  }
}

/**
 * Read-only project-members lookup for pickers that need "project members
 * first" scoping (e.g. AssigneePicker, #875). Many components can call this
 * with the same `projectId` concurrently — the fetch is de-duplicated and
 * cached per project id, so N rows of the same project fire one request, not
 * N. Pass `undefined` to opt out (no fetch, `loading: false, items: []`).
 */
export function useProjectMembersLookup(projectId: number | undefined): MembersLookup {
  const sig = projectId !== undefined ? getCacheSignal(projectId) : null;
  useEffect(() => {
    if (projectId === undefined) return;
    const current = getCacheSignal(projectId);
    if (current.value.loading && !cacheInFlight.has(projectId)) {
      cacheInFlight.add(projectId);
      void loadMembersCached(projectId);
    }
  }, [projectId]);
  if (!sig) return { loading: false, items: [] };
  return sig.value;
}

export interface MembersLookup {
  loading: boolean;
  items: ProjectMember[];
}
